import { useState, useEffect } from "react";
import styles from "./DisableProjectForm.module.css";
import Button from "../../../components/UI/Button/Button";
import FeedbackModal from "../../../components/UI/FeedbackModal/FeedbackModal";
import {
  getAllProjects,
  toggleDisableProject,
} from "../../../services/client-project-service";

export default function DisableProjectForm() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const result = await getAllProjects();
      if (result.success) {
        setProjects(result.data || []);
      } else {
        setModalType("error");
        setModalTitle("Error");
        setModalMessage(result.error || "Failed to load projects");
        setModalOpen(true);
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Failed to load projects");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectSelect = (e) => {
    const projectId = e.target.value;
    const project = projects.find((p) => p._id === projectId);
    setSelectedProject(project || null);
  };

  const handleToggleDisable = async () => {
    if (!selectedProject) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Please select a project");
      setModalOpen(true);
      return;
    }

    setIsToggling(true);

    try {
      const result = await toggleDisableProject(selectedProject._id);

      if (result.success) {
        const updatedProjectData = result.data;
        const isDisabled = updatedProjectData?.isDisabled ?? false;

        setModalType("success");
        setModalTitle("Success");
        setModalMessage(
          isDisabled
            ? `Project "${selectedProject.title || selectedProject._id}" has been disabled`
            : `Project "${selectedProject.title || selectedProject._id}" has been enabled`,
        );
        setModalOpen(true);
        await loadProjects();

        const updatedProject = projects.find(
          (p) => p._id === selectedProject._id,
        );
        if (updatedProject) {
          setSelectedProject({ ...updatedProject, isDisabled });
        } else if (updatedProjectData) {
          setSelectedProject({ ...selectedProject, isDisabled });
        }
      } else {
        throw new Error(result.error || "Failed to toggle project status");
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(error.message || "Failed to toggle project status");
      setModalOpen(true);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <>
      <div className={styles.projectForm}>
        <div className={styles.formGroup}>
          <label htmlFor="projectSelect" className={styles.label}>
            Select Project *
          </label>
          <select
            id="projectSelect"
            name="projectSelect"
            value={selectedProject?._id || ""}
            onChange={handleProjectSelect}
            className={styles.select}
            required
            disabled={isLoading || isToggling}
          >
            <option value="">
              {isLoading ? "Loading projects..." : "Select a project..."}
            </option>
            {projects.map((project) => (
              <option key={project._id} value={project._id}>
                {project.title || project._id}{" "}
                {project.isDisabled ? "(Disabled)" : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedProject && (
          <div className={styles.formGroup}>
            <div
              style={{
                padding: "16px",
                background: selectedProject.isDisabled ? "#fef3c7" : "#d1fae5",
                border: `1px solid ${selectedProject.isDisabled ? "#fde68a" : "#a7f3d0"}`,
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  color: selectedProject.isDisabled ? "#92400e" : "#065f46",
                  fontSize: "16px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: selectedProject.isDisabled
                      ? "#f59e0b"
                      : "#10b981",
                  }}
                />
                Current Status:{" "}
                {selectedProject.isDisabled ? "Disabled" : "Enabled"}
              </h3>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Title:</strong> {selectedProject.title || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Type:</strong> {selectedProject.type || "N/A"}
              </p>
              {selectedProject.description && (
                <p
                  style={{
                    margin: "4px 0",
                    color: "#374151",
                    fontSize: "14px",
                  }}
                >
                  <strong>Description:</strong>{" "}
                  {selectedProject.description.substring(0, 100)}
                  {selectedProject.description.length > 100 ? "..." : ""}
                </p>
              )}
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="default"
            onClick={handleToggleDisable}
            disabled={!selectedProject || isToggling || isLoading}
            style={{
              background: selectedProject?.isDisabled
                ? "#10b981"
                : selectedProject
                  ? "#f59e0b"
                  : undefined,
              borderColor: selectedProject?.isDisabled
                ? "#10b981"
                : selectedProject
                  ? "#f59e0b"
                  : undefined,
            }}
          >
            {isToggling
              ? "Updating..."
              : selectedProject?.isDisabled
                ? "Enable Project"
                : "Disable Project"}
          </Button>
        </div>
      </div>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        confirmText="Close"
      />
    </>
  );
}
